import { FC } from 'react';
import { Col } from 'antd';
import { ControlType } from './config';
// 各类型组件
import Radio from './components/Radio';
import Checkbox from './components/Checkbox';
import DropdownSelect from './components/DropdownSelect';
import Input from './components/Input';
import TextareaInput from './components/TextareaInput';
import Upload from './components/Upload';
import NumberInput from './components/NumberInput';
import Time from './components/Time';
import Address from './components/Address/index';
import Provinces from './components/Provinces';
import Footprint from './components/Footprint';
import Surround from './components/Surround';
import ContendInfo from './components/ContendInfo';
import BusinessPlanning from './components/BusinessPlanning';
import SaleAmount from './components/SaleAmount';
import BusinessInfo from './components/BusinessInfo';
import DailyFlow from './components/DailyFlow';
import RefConversion from './components/RefConversion';
import BenchBrands from './components/BenchBrands';
import CalculationJNP from './components/CalculationJNP';
import EstimateSalesKWKCom from './components/EstimateSalesKWKCom';
import EstimateSalesYHT from './components/EstimateSalesYHT';
import PopulationFlowYHT from './components/PopulationFlowYHT';
import EstimateSalesTwo from './components/YHT/EstimateSalesTwo';
import EstimateSalesPersonal from './components/YHT/EstimateSalesPersonal';
import EstimateSalesMTMean from './components/YHT/EstimateSalesMTMean';
import EstimateSalesMTMax from './components/YHT/EstimateSalesMTMax';
import EstimateSalesConclusion from './components/YHT/EstimateSalesConclusion';
import EstimateSalesDelivery from './components/YHT/EstimateSalesDelivery';
// hooks
import { useAccessIsDisabled } from './hooks/useAccessIsDisabled';
import { useAccessIsRequired } from './hooks/useAccessIsRequired';
import { useAccessIsShow } from './hooks/useAccessIsShow';
import PassengerFlowCounting from './components/PassengerFlow';
import CompetitiveConditions from './components/CompetitiveConditions';

/**
 * @Description 一个动态组件
 * 区分不同的组件类型
 */
const PropertyComponent: FC<{
  item,
  form,
  wholeFormData,
  /** 是否机会点表单，加盟商时false */
  isChancepoint: boolean,
  isEvaluation: boolean,
  addressChange,
  /** 地图移动事件 */
  mapMoveChange,
  /** 地图缩放事件 */
  mapZoomChange,
  updateCompValue,
  updateRelateShow,
  /** 是否支持直接提交审批 1:支持 2:不支持，默认值是2 */
  supportDirectApproval,
  /** 在编辑状态下，踩点组件是否已有数据（已有数据则设置表单不可读） */
  isFootprintValue: boolean,
  businessPlanValue,
  businessInfoValue,
  showHintStr,
  /** 是否有区域保护 */
  isAreaProtect,
  /** 地址组件的值 */
  // addressVal,
  salesFactorOfKWK,
  setSalesFactorOfKWK,
  isEditedBasicChancePointName,
  setEditedBasicChancePointName,
  tiledFormDataRef,
  customGatheringCategoryForYHT,
  setCustomGatheringCategoryForYHT,
  /** 禁用开店区域点击查看详情 */
  disabledArea,
  /** 编辑状态 */
  isEdit?: boolean,
  /** 拓店任务商圈id */
  expansionTaskClusterId?,
}> = ({
  item,
  form,
  wholeFormData,
  isChancepoint = true, // 是否机会点表单，加盟商时false
  isEvaluation = false,
  addressChange,
  mapMoveChange, // 地图移动事件
  mapZoomChange, // 地图缩放事件
  updateCompValue,
  updateRelateShow,
  supportDirectApproval, // 是否支持直接提交审批 1:支持 2:不支持，默认值是2
  isFootprintValue = false, // 在编辑状态下，踩点组件是否已有数据（已有数据则设置表单不可读）
  businessPlanValue = {},
  businessInfoValue = {},
  showHintStr,
  isAreaProtect, // 是否有区域保护
  // addressVal, // 地址组件的值
  salesFactorOfKWK,
  setSalesFactorOfKWK,
  isEditedBasicChancePointName,
  setEditedBasicChancePointName,
  tiledFormDataRef,
  customGatheringCategoryForYHT,
  setCustomGatheringCategoryForYHT,
  disabledArea, // 禁用开店区域点击查看详情
  isEdit = false, // 编辑状态
  expansionTaskClusterId,
}) => {
  // 独占一行的字段
  const isColFullIdentifications = [
    'basicAddress'
  ];
  // 独占一行的组件类型
  const isColFullControlTypes = [
    ControlType.CHECK_BOX.value,
    ControlType.SURROUND_SEARCH.value,
    ControlType.CONTEND_INFO.value,
    ControlType.COMPETITIVE_CONDITIONS.value,
    ControlType.BUSINESS_PLANNING.value,
    ControlType.MATCH_BUSINESS_CIRCLE.value,
    ControlType.DAILY_FLOW_PREDICT.value,
    ControlType.BENCH_MARKING_BRANDS.value,
    ControlType.PROFIT_CAlCULATION_JNP.value,
  ];
  // 定制组件内含多个组件时，外面不能包<Col>
  const targetMultipleComponent = [
    ControlType.DAILY_FLOW_PREDICT.value,
    ControlType.ESTIMATE_SALES_YHT.value,
    ControlType.PASSENGER_FLOW.value
  ];
  // 是否是换行显示
  const isLineFeed = (item: any) => {
    const { templateRestriction } = item;
    if (!templateRestriction) return false;
    try {
      const parsed = JSON.parse(templateRestriction);
      const { nextLine } = parsed || {};
      // 换行显示
      return nextLine;
    } catch (error) {}
    return false;
  };

  /**
   * @description 是否禁用
   */
  const disabled = useAccessIsDisabled({
    supportDirectApproval,
    propertyItem: item,
    isEvaluation,
    isChancepoint,
  });

  /**
   * @description 是否必填
   */
  const required = useAccessIsRequired({
    propertyItem: item,
    isChancepoint,
  });

  /**
   * @description 支持提交审批时是否可见，结合isShow最终决定是否展示
   */
  const isShowWhenApprovalProcess = useAccessIsShow({
    supportDirectApproval,
    propertyItem: item,
    isEvaluation,
    isChancepoint,
  });

  return (
    item.isShow && isShowWhenApprovalProcess ? <>
      {/* 换行显示 */}
      {
        isLineFeed(item) ? (<Col span={24}></Col>) : null
      }
      {/*
        默认两列布局，但是部分场景下需要换行显示
      */}
      {
        // targetMultipleComponent中的定制组件内部包含多个字段值，因为排版问题，所以外层不能包Col
        targetMultipleComponent.includes(item.controlType) ? <>
          {
            item.controlType === ControlType.DAILY_FLOW_PREDICT.value ? <DailyFlow
              form={form}
              propertyItem={item}
              disabled={disabled}
              // required={required}
              updateCompValue={updateCompValue}
            /> : <></>
          }
          { // 益禾堂预估销售额
            item.controlType === ControlType.ESTIMATE_SALES_YHT.value ? <EstimateSalesYHT
              propertyItem={item}
              required={required}
              disabled={disabled}
              form={form}
              tiledFormDataRef={tiledFormDataRef}
            // addressVal={addressVal}
            // wholeFormData={wholeFormData}
            // infoChange={updateCompValue}
            /> : <></>
          }
          {item.controlType === ControlType.PASSENGER_FLOW.value ? <PassengerFlowCounting
            form={form}
            propertyItem={item}
            disabled={disabled}
            required={required}
            tiledFormDataRef={tiledFormDataRef}
            isEdit={isEdit}
          /> : <></>}
        </> : <Col span={
          isColFullIdentifications.includes(item.identification) ||
        isColFullControlTypes.includes(item.controlType) ? 24 : 12}>
          { // 单选组件,有两种展现形式,选项小于3项时用Raido，否则用select
            item.controlType === ControlType.SINGLE_RADIO.value &&
          (Array.isArray(item.propertyConfigOptionVOList) &&
          item.propertyConfigOptionVOList.length > 0 &&
          item.propertyConfigOptionVOList.length < 3 &&
          // 产品甜瓜：要求“加盟费-益禾堂”必须是下拉框
          item.identification !== 'franchiseFeeYHT'
            ? <Radio
              propertyItem={item}
              form={form}
              disabled={disabled}
              required={required}
              updateRelateShow={updateRelateShow}
              showHintStr={showHintStr}
              salesFactorOfKWK={salesFactorOfKWK}
              setSalesFactorOfKWK={setSalesFactorOfKWK}
            />
            : <DropdownSelect
              propertyItem={item}
              disabled={disabled}
              required={required}
              showHintStr={showHintStr}
              form={form}
              salesFactorOfKWK={salesFactorOfKWK}
              setSalesFactorOfKWK={setSalesFactorOfKWK}
              // updateRelateShow={updateRelateShow}
            />)
          }
          {
            // 多选组件
            item.controlType === ControlType.CHECK_BOX.value && <Checkbox propertyItem={item}
              disabled={disabled}
              required={required}
              showHintStr={showHintStr}
              // updateRelateShow={updateRelateShow}
              form={form}/>
          }
          {
            // 输入框组件
            item.controlType === ControlType.INPUT.value && <Input
              propertyItem={item}
              disabled={disabled}
              required={required}
              // form={form}
              onChange={(val) => {
                // 如果是机会点名称，且被编辑过时，不会在机会点编辑中点击poi图例时赋值，2024.10.17定制化逻辑-甜瓜
                if (!val.target.value) {
                  setEditedBasicChancePointName?.(false);
                } else if (item.identification === 'basicChancePointName') {
                  setEditedBasicChancePointName?.(true);
                }
              }}
            />
          }
          {
            // 文本框组件
            item.controlType === ControlType.TEXT_AREA.value && <TextareaInput
              propertyItem={item}
              disabled={disabled}
              required={required}
              form={form}
              tiledFormDataRef={tiledFormDataRef}
            />
          }
          {
            // 上传组件
            item.controlType === ControlType.UPLOAD.value && <Upload
              propertyItem={item}
              disabled={disabled}
              required={required}/>
          }
          {
            // 数字输入框组件
            item.controlType === ControlType.INPUT_NUMBER.value && <NumberInput
              propertyItem={item}
              disabled={disabled}
              required={required}
              showHintStr={showHintStr}
              salesFactorOfKWK={salesFactorOfKWK}
              setSalesFactorOfKWK={setSalesFactorOfKWK}
              form={form}
              tiledFormDataRef={tiledFormDataRef}
            />
          }
          {
            // 日期组件
            item.controlType === ControlType.TIME.value && <Time
              propertyItem={item}
              disabled={disabled}
              required={required}/>
          }
          {
            // 详细地址组件：详细地址+经纬度+地图商圈+经营门店
            item.controlType === ControlType.ADDRESS.value && <Address
              form={form}
              formItemData={item}
              wholeFormData={wholeFormData}
              disabled={disabled}
              required={required}
              addressChange={addressChange}
              isAreaProtect={isAreaProtect}
              mapMoveChange={mapMoveChange}
              mapZoomChange={mapZoomChange}
              isChancepoint={isChancepoint}
              isEditedBasicChancePointName={isEditedBasicChancePointName}
              updateCompValue={updateCompValue}
              expansionTaskClusterId={expansionTaskClusterId}
            />
          }
          {
            // 省市区组件（目前的逻辑是省市区处于禁用状态，根据详细地址组件拿到的省市区id进行赋值）
            item.controlType === ControlType.AREA.value && <Provinces
              propertyItem={item}
              required={required}
              config={{ disabled: true }}
            />
          }
          {
            // 踩点组件-0727
            item.controlType === ControlType.FOOTPRINT.value && <Footprint
              form={form}
              disabled={disabled || isFootprintValue}
              required={required}
            />
          }
          {
            // 周边查询（目前的逻辑是周边查询组件内不能修改地址，地址自动从详细地址组件中带出，不可修改。之后详细地址结合周边查询配置，获取周边信息）
            item.controlType === ControlType.SURROUND_SEARCH.value && <Surround
              formItemData={item}
              form={form}
              disabled={disabled}
              required={required}
              surroundChange={updateCompValue}/>
          }
          {
            item.controlType === ControlType.CONTEND_INFO.value && <ContendInfo
              propertyItem={item}
              form={form}
              disabled={disabled}
              required={required}
              contendInfoChange={updateCompValue}
            />
          }
          {
            item.controlType === ControlType.COMPETITIVE_CONDITIONS.value && <CompetitiveConditions
              propertyItem={item}
              form={form}
              disabled={disabled}
              required={required}
              valueChange={updateCompValue}
            />
          }
          {
            // 商圈规划（目前的逻辑是商圈规划组件内不能修改地址，地址自动从详细地址组件中带出，不可修改。之后用详细地址，获取商圈信息）
            item.controlType === ControlType.BUSINESS_PLANNING.value && <BusinessPlanning
              formItemData={item}
              form={form}
              businessPlanValue={businessPlanValue}
              businessPlanChange={updateCompValue}
              disabled={disabled}/>
          }
          {
            // 销售额（目前的逻辑是销售额组件内不能修改地址，地址自动从详细地址组件中带出，不可修改。之后用详细地址，获取销售额）
            item.controlType === ControlType.SALE_AMOUNT.value && <SaleAmount
              form={form}
              formItemData={item}
              required={required}/>
          }
          {
            // 商圈信息（目前的逻辑是商圈规划组件内不能修改地址，地址自动从详细地址组件中带出，不可修改。之后用详细地址，获取商圈信息）
            item.controlType === ControlType.MATCH_BUSINESS_CIRCLE.value && <BusinessInfo
              formItemData={item}
              form={form}
              disabled={disabled}
              businessInfoValue={businessInfoValue}
              setCustomGatheringCategoryForYHT={setCustomGatheringCategoryForYHT}
              disabledArea={disabledArea}
            />
          }
          {
            // 定制组件，配合益禾堂集客点的商圈类型 https://confluence.lanhanba.com/pages/viewpage.action?pageId=132273933
            item.controlType === ControlType.POPULATION_FLOW_YHT.value && <PopulationFlowYHT
              propertyItem={item}
              form={form}
              disabled={disabled}
              required={required}
              customGatheringCategoryForYHT={customGatheringCategoryForYHT}
            />
          }
          {
            // 参考转化率、参考租金
            [ControlType.REFERENCE_CONVERSION.value, ControlType.REFERENCE_RENT.value].includes(item.controlType) && <RefConversion propertyItem={item} />
          }
          {
            // 对标品牌
            item.controlType === ControlType.BENCH_MARKING_BRANDS.value && <BenchBrands
              propertyItem={item}
              form={form}
              disabled={disabled}
              required={required}
              infoChange={updateCompValue}
            />
          }
          {
            // 基诺浦盈亏测算组件
            item.controlType === ControlType.PROFIT_CAlCULATION_JNP.value && <CalculationJNP
              formItemData={item}
              form={form}
              disabled={disabled}
              wholeFormData={wholeFormData}
              infoChange={updateCompValue}
            />
          }
          {
            // 卡旺卡预计门店销售额
            item.controlType === ControlType.ESTIMATE_SALES_KWK.value && <EstimateSalesKWKCom
              propertyItem={item}
              // form={form}
              // addressVal={addressVal}
              // wholeFormData={wholeFormData}
              // infoChange={updateCompValue}
            />
          }
          {/* 益禾堂定制组件-start
          https://confluence.lanhanba.com/pages/viewpage.action?pageId=132274569 */}
          {
            item.controlType === ControlType.ESTIMATE_SALES_TWO_YHT.value && <EstimateSalesTwo
              propertyItem={item}
              required={required}
            />
          }
          {
            item.controlType === ControlType.ESTIMATE_SALES_PERSONAL_YHT.value && <EstimateSalesPersonal
              tiledFormDataRef={tiledFormDataRef}
              propertyItem={item}
              required={required}
              disabled={disabled}
            />
          }
          {
            item.controlType === ControlType.ESTIMATE_SALES_MT_MEAN_YHT.value && <EstimateSalesMTMean
              propertyItem={item}
              disabled={disabled}
              required={required}
            />
          }
          {
            item.controlType === ControlType.ESTIMATE_SALES_MT_MAX_YHT.value && <EstimateSalesMTMax
              propertyItem={item}
              disabled={disabled}
              required={required}
            />
          }
          {
            item.controlType === ControlType.ESTIMATE_SALES_DELIVERY_YHT.value && <EstimateSalesDelivery
              propertyItem={item}
              required={required}
            />
          }
          {
            item.controlType === ControlType.ESTIMATE_SALES_CONCLUSION_YHT.value && <EstimateSalesConclusion
              propertyItem={item}
              form={form}
              disabled={disabled}
              required={required}
              tiledFormDataRef={tiledFormDataRef}
            />
          }
          {/* 益禾堂定制组件-end*/}
        </Col>}
    </> : null
  );
};

export default PropertyComponent;
